import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { parseUserAgent } from '../../utils/parseUserAgent';
import { PolicyService } from '../../services/api';

/**
 * Subset of an assignment row exposing the fields needed to render
 * sign-time provenance. Fields are optional because (a) older rows
 * predate the consent UI and (b) the same component renders both the
 * signed and declined cases.
 */
export interface SignTimeDetailsData {
    /** Assignment status — drives which sections are surfaced. */
    status: 'pending' | 'signed' | 'declined' | 'revoked';
    /** ISO-8601 timestamp at which the policy was signed. */
    acknowledgedAt?: string;
    /** IP captured at sign time. */
    acknowledgedIp?: string;
    /** User-Agent header captured at sign time. */
    acknowledgedUserAgent?: string;
    /** Whether the signer affirmatively consented to electronic signature. */
    electronicSignatureConsented?: boolean;
    /** Consent text version the signer agreed to. */
    consentVersion?: string;
    /** ISO-8601 timestamp at which the assignment was declined. */
    declinedAt?: string;
    /** Optional free-text reason supplied at decline time. */
    declineReason?: string;
    /** IP captured at decline time. */
    declinedIp?: string;
    /** User-Agent header captured at decline time. */
    declinedUserAgent?: string;
}

/** Props for {@link SignTimeDetails}. */
export interface SignTimeDetailsProps {
    /** The sign-time provenance fields to render. */
    details: SignTimeDetailsData;
    /**
     * The assignment ID this row represents. When provided AND the row
     * is in a terminal state (signed / declined), a Download Certificate
     * button is rendered so admins and signers can pull the audit-trail
     * PDF. Optional so existing callers continue to render unchanged.
     */
    assignmentId?: string;
}

/** UTC ISO timestamp with a tooltip exposing the raw value. */
function TimestampWithTooltip({ iso }: { iso: string }) {
    const local = new Date(iso).toLocaleString();
    return (
        <Tooltip title={iso} arrow>
            <span>{local}</span>
        </Tooltip>
    );
}

/** A single label / value row in the details panel. */
function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                {label}
            </Typography>
            <Typography variant="body2" component="span" sx={{ wordBreak: 'break-word' }}>
                {children}
            </Typography>
        </Box>
    );
}

/**
 * Renders the sign-time provenance for a single assignment: timestamp +
 * IP + parsed user agent + electronic-signature consent (when present),
 * and decline metadata (when the row is declined). The raw UA string is
 * tucked inside a `<details>` element so the auditor can copy it
 * verbatim without cluttering the headline.
 *
 * Used by the admin policy detail page, the admin acknowledgments page,
 * and the signer portal — all three reuse the same component for visual
 * consistency.
 */
function SignTimeDetails({ details, assignmentId }: SignTimeDetailsProps) {
    const hasSigned = Boolean(details.acknowledgedAt) || Boolean(details.acknowledgedIp) || Boolean(details.acknowledgedUserAgent);
    const hasDeclined =
        details.status === 'declined' ||
        Boolean(details.declinedAt) ||
        Boolean(details.declineReason) ||
        Boolean(details.declinedIp) ||
        Boolean(details.declinedUserAgent);
    const hasConsent = details.electronicSignatureConsented === true || Boolean(details.consentVersion);
    const isTerminal = details.status === 'signed' || details.status === 'declined';
    const showDownload = isTerminal && Boolean(assignmentId);

    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);

    const handleDownload = async () => {
        if (!assignmentId) return;
        setDownloadError(null);
        setDownloading(true);
        try {
            await PolicyService.downloadCertificate(assignmentId);
        } catch {
            setDownloadError('Failed to download the certificate. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    if (!hasSigned && !hasDeclined && !hasConsent) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    No sign-time information yet — the assignment is still pending.
                </Typography>
            </Box>
        );
    }

    const ackUaParsed = details.acknowledgedUserAgent ? parseUserAgent(details.acknowledgedUserAgent) : null;
    const declineUaParsed = details.declinedUserAgent ? parseUserAgent(details.declinedUserAgent) : null;

    return (
        <Stack spacing={2.5} sx={{ p: 1 }}>
            {hasSigned && (
                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Signed
                    </Typography>
                    <Stack spacing={0.75}>
                        <FieldRow label="Signed at">{details.acknowledgedAt ? <TimestampWithTooltip iso={details.acknowledgedAt} /> : '—'}</FieldRow>
                        <FieldRow label="IP address">{details.acknowledgedIp ?? '—'}</FieldRow>
                        <FieldRow label="Browser / OS">{ackUaParsed ? ackUaParsed.parsed : '—'}</FieldRow>
                        {ackUaParsed && (
                            <Box component="details" sx={{ ml: 0, mt: 0.5 }}>
                                <Box component="summary" sx={{ cursor: 'pointer', fontSize: '0.875rem', color: 'text.secondary' }}>
                                    Show raw user agent
                                </Box>
                                <Typography
                                    variant="caption"
                                    component="div"
                                    sx={{
                                        mt: 0.5,
                                        p: 1,
                                        fontFamily: 'monospace',
                                        bgcolor: 'action.hover',
                                        borderRadius: 1,
                                        wordBreak: 'break-all',
                                    }}
                                >
                                    {ackUaParsed.raw}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </Box>
            )}

            {hasConsent && (
                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Electronic signature consent
                    </Typography>
                    <Stack spacing={0.75}>
                        <FieldRow label="Consented">{details.electronicSignatureConsented === true ? 'Yes' : 'No'}</FieldRow>
                        {details.consentVersion && <FieldRow label="Version">{details.consentVersion}</FieldRow>}
                    </Stack>
                </Box>
            )}

            {hasDeclined && (
                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Declined
                    </Typography>
                    <Stack spacing={0.75}>
                        <FieldRow label="Declined at">{details.declinedAt ? <TimestampWithTooltip iso={details.declinedAt} /> : '—'}</FieldRow>
                        <FieldRow label="IP address">{details.declinedIp ?? '—'}</FieldRow>
                        <FieldRow label="Browser / OS">{declineUaParsed ? declineUaParsed.parsed : '—'}</FieldRow>
                        {details.declineReason && <FieldRow label="Reason">{details.declineReason}</FieldRow>}
                        {declineUaParsed && (
                            <Box component="details" sx={{ ml: 0, mt: 0.5 }}>
                                <Box component="summary" sx={{ cursor: 'pointer', fontSize: '0.875rem', color: 'text.secondary' }}>
                                    Show raw user agent
                                </Box>
                                <Typography
                                    variant="caption"
                                    component="div"
                                    sx={{
                                        mt: 0.5,
                                        p: 1,
                                        fontFamily: 'monospace',
                                        bgcolor: 'action.hover',
                                        borderRadius: 1,
                                        wordBreak: 'break-all',
                                    }}
                                >
                                    {declineUaParsed.raw}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </Box>
            )}

            {showDownload && (
                <Box>
                    <Button variant="outlined" size="small" onClick={handleDownload} disabled={downloading}>
                        {downloading ? 'Downloading…' : 'Download Certificate'}
                    </Button>
                    {downloadError && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }} role="alert">
                            {downloadError}
                        </Typography>
                    )}
                </Box>
            )}
        </Stack>
    );
}

export default SignTimeDetails;
